import { useLocation } from 'react-router';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { sortPositionLabels } from 'components/Dashboard/Analytics/sortPositionLabels';
dayjs.extend(isSameOrAfter);

const useCompileMentionsDataFunctions = ({ chartRef = {}, data = {}, analyticsState, rawData = {} }) => {
  const { chartDataOptionSelectedTweets, chartDataOptionSelected } = analyticsState ?? {};
  const location = useLocation();

  const createChartDataKeys = () => {
    if (chartRef.current && !!data?.value) {
      let sections = [];
      Object.keys(data.value).forEach((item, index) => {
        if (chartRef.current?.isDatasetVisible(index)) {
          let info = chartRef.current.getDatasetMeta(index);
          sections.push(info.label);
        }
      });
      return sortPositionLabels(sections);
    } else {
      return [];
    }
  };
  const chartDataKeys = createChartDataKeys();

  const compileTotalsWithFilters = () => {
    const localChartDataKeys = createChartDataKeys();
    let activeItems = Object.keys(data?.value).filter((item) => localChartDataKeys.includes(item));
    let newValue = {};
    activeItems.forEach((item) => (newValue[item] = data.value[item]));
    let totalValues = compileData({ value: newValue }, analyticsState).value;
    return totalValues;
  };

  const createSectionValues = (rightPanelState) => {
    const { dataKey, date, noResults } = rightPanelState;
    if (dataKey && date) {
      const TweetsSection = location.pathname.includes('/analytics/tweets');
      const chartOptionName = TweetsSection ? chartDataOptionSelectedTweets : chartDataOptionSelected;
      const totalCell = dataKey === 'totalCell';
      const contributionGraph = chartOptionName === 'Contribution type' || chartOptionName === 'Overall';
      let sections = TweetsSection
        ? []
        : ['Spoken parliamentary contributions', 'Written questions', 'EDMs', 'Committee contributions'];
      if (import.meta.env.VITE_APP_ENABLE_TWITTER === 'true') {
        sections = [...sections, 'Tweets', 'Retweets'];
      }

      const values = [];
      if (!noResults) {
        if (totalCell) {
          sections.forEach((section) => {
            let number = 0;
            if (Array.isArray(rawData[section])) {
              let point = rawData[section]?.find((point) => point?.date === date)?.value;
              if (point && contributionGraph) {
                number += point;
              }
            } else if (rawData[section]) {
              Object.keys(rawData[section]).forEach((key) => {
                let point = rawData[section]?.[key]?.find((point) => point.date === date)?.value;
                if (point && (chartDataKeys?.includes(key) || contributionGraph)) {
                  number += point;
                }
              });
            }
            values.push({ name: section, value: number });
          });
        } else if (contributionGraph && dataKey === 'Written questions') {
          // Written questions are a single merged bar in Contribution type/Overall, but rawData
          // still keeps one key per nation - show those as the panel's sub-section buttons.
          ['Written questions', 'Written questions (Wales)', 'Written questions (Scotland)'].forEach((section) => {
            let number = 0;
            let point = rawData[section]?.find((point) => point?.date === date)?.value;
            if (point) {
              number += point;
            }
            values.push({ name: section, value: number });
          });
        } else {
          sections.forEach((section) => {
            let number = 0;
            let point = rawData[section]?.[dataKey]?.find((point) => point?.date === date)?.value;
            if (point) {
              number += point;
            }
            values.push({ name: section, value: number });
          });
        }
      }
      return values.filter((item) => item.value > 0);
    }
  };

  return { chartDataKeys, compileTotalsWithFilters, createSectionValues, compileData };
};

const compileData = (data, analyticsState, addEmptyValues) => {
  let datasetsInit = JSON.parse(JSON.stringify(data.value));
  let datasets = [];
  Object.keys(datasetsInit).forEach((dataset) => {
    datasets.push(datasetsInit[dataset]);
  });
  let compiledData = datasets.flat().reduce((acc, item) => {
    let dateIndex = acc.findIndex((point) => point.date === item.date);
    if (dateIndex >= 0) {
      let { value } = acc[dateIndex];
      let newValue = item.value + value;
      acc[dateIndex].value = newValue;
    } else {
      acc.push(item);
    }
    return acc;
  }, []);

  //OP: add empty values for months if months labeles are not complete
  if (analyticsState.currentView === 'Month' && addEmptyValues) {
    let startMonth = dayjs(dayjs().subtract(5, 'month').startOf('day').format('YYYY-MM-DD'))
      .startOf('month')
      .format('YYYY-MM-DD');

    // Check for earlier dates in compiledData
    compiledData.forEach((item) => {
      if (dayjs(item.date).isBefore(startMonth)) {
        startMonth = dayjs(item.date).startOf('month').format('YYYY-MM-DD');
      }
    });

    const endDay = dayjs().add(1, 'day').format('YYYY-MM-DD');
    const allMonths = [];
    let currentMonth = dayjs(startMonth);

    while (currentMonth.isBefore(endDay)) {
      allMonths.push(currentMonth.format('YYYY-MM-DD'));
      currentMonth = currentMonth.add(1, 'month');
    }
    compiledData = allMonths.map((month) => {
      const existing = compiledData.find((item) => item.date === month);
      return existing || { date: month, value: 0 };
    });
  }

  let filteredCompiledData = compiledData.filter((item) => {
    if (analyticsState.activeDateInterval.name === 'Custom') {
      if (analyticsState.currentView !== 'Day') {
        return (
          new Date(item.date) >=
            new Date(
              dayjs(analyticsState.activeDateInterval.filter[0]).startOf(analyticsState.currentView.toLowerCase())
            ) &&
          new Date(item.date) <=
            new Date(dayjs(analyticsState.activeDateInterval.filter[1]).endOf(analyticsState.currentView.toLowerCase()))
        );
      } else {
        return (
          new Date(item.date) >= new Date(analyticsState.activeDateInterval.filter[0]) &&
          new Date(item.date) <= new Date(analyticsState.activeDateInterval.filter[1])
        );
      }
    } else {
      return true;
    }
  });

  return {
    raw: filteredCompiledData,
    value: filteredCompiledData.map((item) => item.value),
  };
};

export { compileData };

export default useCompileMentionsDataFunctions;
